/**
 * What the equipment box draws: the tiles, or which of four other states it is in.
 *
 * `GET /equipment` is a READ, so the box has a wait and a failure of its own — and "the search
 * matched nothing" and "the taxonomy is empty" are two different sentences with two different ways
 * out. Five states, decided once here, never a branch on `length === 0` in the component.
 *
 * The search is diacritic-blind through `shared/lib/foldPolish`, the fold the library's filter panel
 * already searches its options with — „lydki" reaches „Łydki" here for the same reason it does there.
 * The order is the endpoint's (most-used first), which is also the canon's.
 */

import type { ExerciseEquipment } from '~/entities/exercise-library';
import { foldPolish } from '~/shared/lib/foldPolish';

export interface EquipmentTile {
  readonly piece: ExerciseEquipment;
  readonly selected: boolean;
}

export type EquipmentTilesView =
  | { readonly status: 'pending' }
  | { readonly status: 'error' }
  | { readonly status: 'empty' }
  | { readonly status: 'noMatch' }
  | { readonly status: 'ready'; readonly tiles: readonly EquipmentTile[] };

/** The read as the box needs it: still out, failed, or answered with the taxonomy. */
export type EquipmentRead =
  | { readonly status: 'pending' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly pieces: readonly ExerciseEquipment[] };

/**
 * The query's flags, as the box reads them. Data WINS: a failed background refetch must not replace
 * tiles already on screen with the error; and a retry in flight is a wait, not the old failure.
 */
export function equipmentReadOf(query: {
  readonly data: readonly ExerciseEquipment[] | undefined;
  readonly isError: boolean;
  readonly isFetching: boolean;
}): EquipmentRead {
  if (query.data !== undefined) return { status: 'ready', pieces: query.data };
  return query.isError && !query.isFetching ? { status: 'error' } : { status: 'pending' };
}

export function equipmentTiles(
  read: EquipmentRead,
  search: string,
  selectedIds: readonly number[],
): EquipmentTilesView {
  if (read.status !== 'ready') return read;
  if (read.pieces.length === 0) return { status: 'empty' };
  const term = foldPolish(search);
  const tiles = read.pieces
    .filter((piece) => term === '' || foldPolish(piece.name).includes(term))
    .map((piece) => ({ piece, selected: selectedIds.includes(piece.id) }));
  return tiles.length === 0 ? { status: 'noMatch' } : { status: 'ready', tiles };
}

/**
 * The names the rail echoes for the chosen pieces, in the order they were chosen. Backend data.
 *
 * While the taxonomy is still out or failed, the pieces the movement already names (an edit's, a
 * duplicate's) answer for themselves, so step 2's summary does not claim „bez sprzętu".
 */
export function chosenEquipmentNames(
  read: EquipmentRead,
  selectedIds: readonly number[],
  known: readonly ExerciseEquipment[],
): readonly string[] {
  const pieces = read.status === 'ready' ? read.pieces : known;
  return selectedIds.flatMap((id) => pieces.find((piece) => piece.id === id)?.name ?? []);
}
